'use client';

import React, { useMemo } from 'react';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { useGetEntityProfileConfigQuery } from 'src/api/redux/profileConfiguration';
import {
  buildOrderedSections,
  WidgetRenderer,
} from './index';
import SectionCard from './SectionCard';

interface ProfileConfigContentProps {
  /** When provided, used directly instead of reading from profile context. */
  entityId?: string | number;
  /** Widget codes to exclude from standalone rendering (e.g. rendered in a sidebar instead). */
  excludeStandaloneCodes?: string[];
}

const ProfileConfigContent: React.FC<ProfileConfigContentProps> = ({
  entityId: entityIdProp,
  excludeStandaloneCodes = [],
}) => {
  const { entityDetails } = useProfile();
  const { providerId } = getEntityIdentifiers(entityDetails);

  const entityId = entityIdProp ?? providerId;

  const {
    data: profileConfig,
    isLoading,
    isFetching,
  } = useGetEntityProfileConfigQuery({ entityId }, { skip: !entityId });

  const sections = useMemo(
    () =>
      buildOrderedSections(
        profileConfig?.widgets ?? [],
        excludeStandaloneCodes
      ),
    [profileConfig, excludeStandaloneCodes]
  );

  if (isLoading || isFetching) {
    return (
      <div
        className="flex flex-col gap-4"
        data-testid="profile-config-loading"
        aria-label="Loading profile configuration"
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="bg-white border border-neutral-200 rounded-lg px-5 py-4 flex items-center justify-between gap-3 animate-pulse"
          >
            <div className="flex flex-col gap-2 flex-1">
              <div className="h-4 w-1/3 bg-neutral-200 rounded" />
              <div className="h-3 w-2/3 bg-neutral-100 rounded" />
            </div>
            <div className="h-3 w-3 bg-neutral-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="profile-config-content">
      {sections.map((section) => {
        if (section.type === 'codeset') {
          const { group } = section;
          return (
            <SectionCard
              key={group.codeSet.id}
              label={group.codeSet.label}
              defaultOpen={false}
              data-testid={`code-set-section-${group.codeSet.id}`}
            >
              <div className="flex flex-col gap-4">
                {group.widgets.map((widget) => (
                  <div
                    key={widget.id}
                    data-testid={`widget-card-${widget.code}-${widget.id}`}
                  >
                    <WidgetRenderer widget={widget} />
                  </div>
                ))}
              </div>
            </SectionCard>
          );
        }

        if (section.type === 'plays') {
          const { widget } = section;
          return (
            <div
              key={`plays-${widget.id}`}
              data-testid={`widget-card-${widget.code}-${widget.id}`}
              className="w-full"
            >
              <WidgetRenderer widget={widget} />
            </div>
          );
        }

        const { widget } = section;

        return (
          <div
            key={`${widget.code}-${widget.id ?? widget.display_order}`}
            data-testid={`widget-card-${widget.code}-${widget.id ?? widget.display_order}`}
            className="w-full"
          >
            <WidgetRenderer widget={widget} />
          </div>
        );
      })}
    </div>
  );
};

export default ProfileConfigContent;
